import parseVideo, { addChannelData } from './parsers/video-new';
import parseError, { InternalError } from './parsers/error';

import parseUrl from './parse-url';

import urlTypes from './url-types';

import * as http from '../../utils/http';


const MAX_IDS_IN_REQUEST = 50;

const defaultPaging = {
  size: MAX_IDS_IN_REQUEST,
  cursor: null,
};

const defaultSort = {
  order: 'relevance',
};


export default function (settings, adapterMethods) {

  function _internalGet(videoIds, options = {}) {

    videoIds = _.castArray(videoIds);

    const videosRequests = _(videoIds)
      .uniq()
      .chunk(MAX_IDS_IN_REQUEST)
      .map(function (chunkVideoIds) {
        const params = {
          id: chunkVideoIds.join(','),
        };

        return http.get(`${settings.apiUrl}/videos`, { params })
          .then(_.partialRight(_extractVideoItems, options))
          .then(addVideoCategories);
      })
      .value();

    return Promise.all(videosRequests)
      .then(function (itemsFromResponses) {
        const videos = _.flatten(itemsFromResponses);

        return {
          status: 200,
          data: _.map(videos, parseVideo),
        };
      });
  }


  function _parsePaginatedResponse(response, options = {}) {
    const sort = _.merge({}, defaultSort, options.sort);
    const paging = _.merge({}, defaultPaging, options.paging);

    const {
      noVideoData = false,
      noChannelData = false,
    } = options;

    const videos = _extractVideoItems(response, options);

    const { nextPageToken } = response.data;


    if (noVideoData) {
      return {
        ...response,
        data: _.map(videos, parseVideo),
        sort,
        paging: {
          size: paging.size,
          cursor: nextPageToken || null,
        },
      };
    }

    const videoIds = _getVideoIds(videos);

    const loadMethod = noChannelData ? _internalGet : getWithChannelData;

    return loadMethod(videoIds, options)
      .then(function (videosResponse) {
        return {
          ...videosResponse,
          sort,
          paging: {
            size: paging.size,
            cursor: nextPageToken || null,
          },
        };
      });
  }


  function getByUrl(url, skipVideos = false) {
    return parseUrl(url, skipVideos)
      .then(function ({ id, type }) {
        const options = { noExtraChannelData: true };

        switch (type) {
          case urlTypes.CHANNEL:
            return getByChannelId(id, options);

          case urlTypes.PLAYLIST:
            return getByPlaylistId(id, options);

          case urlTypes.USER_NAME:
            return getByUsername(id, options);

          case urlTypes.VIDEO:
            return getWithChannelData(id, options);

          default:
            return null;
        }
      })
      .catch(parseError);
  }


  function getByChannelId(channelId, options = {}) {
    return adapterMethods.channels.get(channelId, options)
      .then(_.property('data[0].uploadsPlaylistId'))
      .then(_.partialRight(getByPlaylistId, options));
  }


  function getByUsername(username, options) {
    return adapterMethods.channels.list({
        forUsername: username,
      })
      .then(_.property('data[0].externalId'))
      .then(_.partialRight(getByChannelId, options))
      .catch(parseError);
  }


  function getWithChannelData(videoIds, options = {}) {
    return _internalGet(videoIds, options)
      .then(function (response) {
        let videos = response.data;
        const channelIds = _.map(videos, 'channelId');

        return adapterMethods.channels.get(channelIds, options)
          .then(function (channelsResponse) {
            const channelsData = _.keyBy(channelsResponse.data, 'externalId');

            videos = _.map(videos, video => addChannelData(video, channelsData[video.channelId]));

            response = {
              ...response,
              data: videos,
            };

            return response;
          });
      })
      .catch(parseError);
  }


  function _getVideoIds(items) {
    return _(items)
      .map(parseVideo)
      .map('externalId')
      .value();
  }


  function _extractVideoItems(response, options) {
    const { data: { items } } = response;
    const { shouldThrowWhenNoItems = true } = options;

    if (shouldThrowWhenNoItems && _.size(items) === 0) {
      throw new InternalError(404, 'wpm_error.no_external_video_found', 'No external video was found');
    }

    return items;
  }


  function getByPlaylistId(playlistId, options = {}, prevResponse = null) {
    const currentPaging = _.merge({}, defaultPaging, options.paging);

    const pageToken = currentPaging.cursor;
    const maxResults = _.min([currentPaging.size, MAX_IDS_IN_REQUEST]);

    const params = {
      playlistId,
      pageToken,
      maxResults,
    };

    if (!prevResponse) {
      prevResponse = { data: [], paging: currentPaging };
    }

    return http.get(`${settings.apiUrl}/playlistitems`, { params })
      .then(response => _parsePaginatedResponse(response, options))
      .then(function (currentResponse) {

        currentResponse = {
          ...currentResponse,
          paging: {
            ...currentResponse.paging,
            size: prevResponse.paging.size,
          },
          data: [
            ...prevResponse.data,
            ...currentResponse.data,
          ],
        };

        const nextOptions = {
          paging: {
            cursor: currentResponse.paging.cursor,
            size: currentPaging.size - maxResults,
          },
        };

        if (!_.every(nextOptions.paging)) {
          // if size is 0 or cursor is null - return final response
          return currentResponse;
        }

        return getByPlaylistId(playlistId, nextOptions, currentResponse);
      })
      .catch(parseError);
  }


  function search(query, searchParams = {}, options = {}) {
    const sort = _.merge({}, defaultSort, options.sort);
    const paging = _.merge({}, defaultPaging, options.paging);

    const params = {
      q: query,
      order: sort.order,
      pageToken: paging.cursor,
      maxResults: paging.size,
      ...searchParams,
    };

    return http.get(`${settings.apiUrl}/search`, { params })
      .then(response => _parsePaginatedResponse(response, options))
      .catch(parseError);
  }

  function get(videoIds, options = {}) {
    return _internalGet(videoIds, options)
      .catch(parseError);
  }

  function addVideoCategories(videosData) {
    const categoryIds = _(videosData)
      .map('snippet.categoryId')
      .uniq()
      .value();

    return getCategories(categoryIds)
      .then(categories => _.map(videosData, item => {
        const categoryId = _.get(item, 'snippet.categoryId');
        return _.set(
          item,
          'snippet.category',
          _.find(categories, ['id', categoryId]).title
        );
      }))
      .catch(() => videosData);
  }

  function getCategories(categoryIds) {
    if (_.isEmpty(categoryIds)) {
      return Promise.resolve();
    }

    return http.get(settings.apiUrlCategories, { params: {
      ids: categoryIds.join(','),
    } })
      .then(response => {
        const categories = _.get(response, 'data.items');
        return categories.map(category => ({
          id: _.get(category, 'id'),
          title: _.get(category, 'snippet.title'),
        }));
      })
      .catch(_.noop);
  }


  return {
    get,
    getWithChannelData,
    getByUrl,
    getByPlaylistId,
    getByChannelId,
    getByUsername,
    search,
  };
}



// WEBPACK FOOTER //
// ./src/transport/adapters/youtube/videos-new.js